// components/CompanyDropdown.jsx
// Reusable searchable + infinite-scroll company selector
import { useState, useRef, useEffect, useCallback } from 'react';
import { Search, ChevronDown, ChevronUp, X, Building2, Loader2, AlertCircle } from 'lucide-react';
import { useCompanyDropdown } from '../hooks/useCompanyDropdown';

// ─── styles ───────────────────────────────────────────────
const S = {
  wrap:     { position: 'relative', width: '100%', fontFamily: "'DM Sans', system-ui, sans-serif" },
  trigger:  {
    display: 'flex', alignItems: 'center', gap: 8,
    width: '100%', padding: '10px 13px',
    border: '1.5px solid #e2e8f0', borderRadius: 9,
    background: '#fff', cursor: 'pointer', fontSize: 14,
    color: '#1e293b', boxSizing: 'border-box',
    transition: 'border-color .15s, box-shadow .15s',
    outline: 'none',
  },
  triggerOpen: { borderColor: '#3b82f6', boxShadow: '0 0 0 3px rgba(59,130,246,.12)' },
  triggerDisabled: { background: '#f8fafc', cursor: 'not-allowed', opacity: .6 },
  placeholder: { color: '#94a3b8', flex: 1, textAlign: 'left', fontSize: 13 },
  selectedText: { flex: 1, textAlign: 'left', fontSize: 13, fontWeight: 500, color: '#1e293b' },
  clearBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 18, height: 18, borderRadius: '50%',
    background: '#e2e8f0', border: 'none', cursor: 'pointer', flexShrink: 0,
    color: '#64748b',
  },
  dropdown: {
    position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
    background: '#fff', borderRadius: 12,
    border: '1.5px solid #e2e8f0',
    boxShadow: '0 12px 40px rgba(0,0,0,.14)',
    zIndex: 9999, overflow: 'hidden',
    animation: 'dropIn .15s ease',
  },
  searchWrap: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '10px 12px', borderBottom: '1px solid #f1f5f9',
    background: '#fafbfc',
  },
  searchInput: {
    flex: 1, border: 'none', outline: 'none', fontSize: 13,
    background: 'transparent', color: '#1e293b',
  },
  list: { maxHeight: 260, overflowY: 'auto', padding: '4px 0' },
  item: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '9px 14px', cursor: 'pointer',
    transition: 'background .1s', fontSize: 13,
  },
  itemHover: { background: '#f0f7ff' },
  itemSelected: { background: '#eff6ff' },
  avatar: (color) => ({
    width: 28, height: 28, borderRadius: 7, flexShrink: 0,
    background: color, display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff',
  }),
  itemName: { fontWeight: 500, color: '#1e293b', lineHeight: 1.3 },
  itemEmail: { fontSize: 11, color: '#94a3b8', marginTop: 1 },
  loader: { padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, color: '#94a3b8', fontSize: 12 },
  empty: { padding: '24px 14px', textAlign: 'center', color: '#94a3b8', fontSize: 13 },
  errorBox: { padding: '10px 14px', color: '#dc2626', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 },
  sentinelWrap: { height: 1 },
};

// ─── component ────────────────────────────────────────────
const CompanyDropdown = ({
  value,           // selected company id
  onChange,        // (company | null) => void
  placeholder = 'Select company...',
  disabled = false,
  required = false,
  label,
  error: externalError,
}) => {
  const [open, setOpen] = useState(false);
  const [hoverId, setHoverId] = useState(null);
  const wrapRef     = useRef(null);
  const sentinelRef = useRef(null);
  const listRef     = useRef(null);

  const {
    companies, search, setSearch,
    loading, loadingMore, hasMore, loadMore, error: fetchError,
  } = useCompanyDropdown();

  // selected company object
  const selected = companies.find(c => c.id === value) || null;

  // ── close on outside click ─────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── infinite scroll via IntersectionObserver ───────────
  useEffect(() => {
    if (!sentinelRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && hasMore && !loadingMore) loadMore(); },
      { root: listRef.current, threshold: 0.1 }
    );
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [hasMore, loadingMore, loadMore, open]);

  const handleSelect = (company) => {
    onChange(company);
    setOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  const triggerStyle = {
    ...S.trigger,
    ...(open ? S.triggerOpen : {}),
    ...(disabled ? S.triggerDisabled : {}),
    ...(externalError ? { borderColor: '#ef4444' } : {}),
  };

  return (
    <div>
      {label && (
        <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {label}{required && <span style={{ color: '#ef4444', marginLeft: 3 }}>*</span>}
        </label>
      )}

      <div ref={wrapRef} style={S.wrap}>
        {/* Trigger */}
        <button
          type="button"
          disabled={disabled}
          style={triggerStyle}
          onClick={() => !disabled && setOpen(o => !o)}
        >
          {selected ? (
            <>
              <div style={S.avatar(selected.color)}>
                {selected.logo
                  ? <img src={selected.logo} alt="" style={{ width: 28, height: 28, borderRadius: 7, objectFit: 'cover' }} />
                  : selected.name[0]?.toUpperCase()}
              </div>
              <span style={S.selectedText}>{selected.name}</span>
              <button style={S.clearBtn} onClick={handleClear} tabIndex={-1} type="button">
                <X size={10} />
              </button>
            </>
          ) : (
            <>
              <Building2 size={15} color="#94a3b8" />
              <span style={S.placeholder}>{placeholder}</span>
            </>
          )}
          {open
            ? <ChevronUp size={15} color="#94a3b8" style={{ flexShrink: 0 }} />
            : <ChevronDown size={15} color="#94a3b8" style={{ flexShrink: 0 }} />}
        </button>

        {/* External error */}
        {externalError && (
          <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
            <AlertCircle size={11} />{externalError}
          </div>
        )}

        {/* Dropdown */}
        {open && (
          <div style={S.dropdown}>
            {/* Search input */}
            <div style={S.searchWrap}>
              <Search size={14} color="#94a3b8" />
              <input
                autoFocus
                type="text"
                placeholder="Search companies..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={S.searchInput}
              />
              {search && (
                <button type="button" onClick={() => setSearch('')} style={{ ...S.clearBtn, background: 'transparent' }}>
                  <X size={12} />
                </button>
              )}
            </div>

            {/* List */}
            <div ref={listRef} style={S.list}>
              {/* Fetch error */}
              {fetchError && (
                <div style={S.errorBox}><AlertCircle size={13} />{fetchError}</div>
              )}

              {/* Initial loading */}
              {loading && !companies.length && (
                <div style={S.loader}>
                  <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                  Loading companies...
                </div>
              )}

              {/* Empty state */}
              {!loading && !fetchError && companies.length === 0 && (
                <div style={S.empty}>
                  <Building2 size={28} style={{ opacity: .2, marginBottom: 6, display: 'block', margin: '0 auto 6px' }} />
                  {search ? `No results for "${search}"` : 'No companies found'}
                </div>
              )}

              {/* Items */}
              {companies.map(c => (
                <div
                  key={c.id}
                  style={{
                    ...S.item,
                    ...(hoverId === c.id ? S.itemHover : {}),
                    ...(value === c.id ? S.itemSelected : {}),
                  }}
                  onMouseEnter={() => setHoverId(c.id)}
                  onMouseLeave={() => setHoverId(null)}
                  onClick={() => handleSelect(c)}
                >
                  <div style={S.avatar(c.color)}>
                    {c.logo
                      ? <img src={c.logo} alt="" style={{ width: 28, height: 28, borderRadius: 7, objectFit: 'cover' }} />
                      : c.name[0]?.toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={S.itemName}>{c.name}</div>
                    {c.email && <div style={S.itemEmail}>{c.email}</div>}
                  </div>
                  {value === c.id && (
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#3b82f6', flexShrink: 0 }} />
                  )}
                </div>
              ))}

              {/* Infinite scroll sentinel */}
              <div ref={sentinelRef} style={S.sentinelWrap} />

              {/* Load more spinner */}
              {loadingMore && (
                <div style={S.loader}>
                  <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />
                  Loading more...
                </div>
              )}

              {/* End of list */}
              {!hasMore && companies.length > 0 && (
                <div style={{ padding: '8px 14px', fontSize: 11, color: '#cbd5e1', textAlign: 'center' }}>
                  All {companies.length} companies loaded
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes dropIn { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:translateY(0); } }
        @keyframes spin   { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default CompanyDropdown;